import { useEffect, useMemo, useRef } from 'react';
import {
  infiniteQueryOptions,
  useInfiniteQuery,
  type QueryClient,
} from '@tanstack/react-query';
import {
  handleGetDesignPointStudiesStudyIdPointsDpIdGetOptions,
  handleListPointsStudiesStudyIdPointsGetInfiniteOptions,
  handleListPointsStudiesStudyIdPointsGetOptions,
} from '@/api/generated/@tanstack/react-query.gen';
import type { DesignPointResponse } from '@/api/generated/types.gen';
import { TIMEOUTS } from '@/constants';
import { TERMINAL_DESIGN_POINT_STATUSES } from '@/routes/_app/simops/-data/constants';

interface UseStudyDesignPointsOptions {
  /** Defaults to `true`. Set false when the parent query (e.g. study fetch) isn't ready. */
  enabled?: boolean;
  /**
   * When `true`, polls at `TIMEOUTS.STUDY_POLL_INTERVAL` regardless of DP
   * status. When `false`/omitted, polls only while at least one DP is still
   * non-terminal — so a finished study eventually stops polling but transient
   * DPs continue to refresh.
   */
  isActive?: boolean;
}

/**
 * Infinite-query options for a study's paginated design points. Shared by
 * `useStudyDesignPoints` and the `/simops/$studyId` route loader so both
 * read and warm the same cache entry.
 */
export function studyDesignPointsInfiniteOptions(studyId: string) {
  return infiniteQueryOptions({
    ...handleListPointsStudiesStudyIdPointsGetInfiniteOptions({
      path: { study_id: studyId },
      query: { limit: 100 },
    }),
    // First-page param shaped as the hey-api Pick variant so the generated
    // queryFn routes through `createInfiniteParams` cleanly. (Plain `null`
    // crashes there: `typeof null === 'object'` selects the structured-page
    // branch, then `.body` is dereferenced on null.)
    initialPageParam: {
      path: { study_id: studyId },
      query: { cursor: null },
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
}

/**
 * Fetches every design point in a study by auto-paginating through
 * `next_cursor`. Single source of truth for the paginated-points fetch so
 * `$studyId.tsx` and `StudyMeshChrome.tsx` share both code and cache.
 *
 * Backend caps `/studies/{id}/points` at `MAX_PAGE_SIZE=100`, so any
 * non-trivial Sobol run needs cursor paging. Returns the flattened items
 * plus the raw query for callers that need `isLoading`/`isError`.
 */
export function useStudyDesignPoints(
  studyId: string,
  { enabled = true, isActive = false }: UseStudyDesignPointsOptions = {},
) {
  const pointsQuery = useInfiniteQuery({
    ...studyDesignPointsInfiniteOptions(studyId),
    enabled,
    refetchInterval: (query) => {
      if (isActive) return TIMEOUTS.STUDY_POLL_INTERVAL;
      const pages = query.state.data?.pages ?? [];
      let hasAny = false;
      for (const page of pages) {
        for (const dp of page.items) {
          hasAny = true;
          if (!TERMINAL_DESIGN_POINT_STATUSES.has(dp.status)) {
            return TIMEOUTS.STUDY_POLL_INTERVAL;
          }
        }
      }
      return hasAny ? false : TIMEOUTS.STUDY_POLL_INTERVAL;
    },
  });

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = pointsQuery;
  const lastCursor = pointsQuery.data?.pages.at(-1)?.next_cursor;
  // Defends against a backend bug where `next_cursor` doesn't advance — the
  // effect would otherwise refetch the same page forever. Re-fires naturally
  // when a fresh page appends a new cursor (e.g. growth mid-study).
  const lastCursorRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    if (lastCursor === lastCursorRef.current) return;
    lastCursorRef.current = lastCursor;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, lastCursor]);

  const items = useMemo<DesignPointResponse[]>(
    () => (pointsQuery.data?.pages ?? []).flatMap((p) => p.items),
    [pointsQuery.data],
  );

  return { items, query: pointsQuery };
}

/** The largest page the backend serves (`MAX_PAGE_SIZE`). Asking for it is
 *  what keeps {@link fetchAllDesignPoints} to one request on most studies. */
const PAGE_SIZE = 100;

/** A ceiling on the walk, so a `next_cursor` that never advances cannot spin
 *  forever. 50 pages is 5 000 points — above `matrix.ts`'s own 5 000-row cap
 *  on a study. */
const MAX_PAGES = 50;

/**
 * The pages of a study's design points the page has already walked into the
 * INFINITE entry, or undefined when it has none. A cache read only — never a
 * request — so a caller that must not fetch (the assistant's view tool) can
 * share it with the ones that fall back to fetching.
 */
export function cachedDesignPointPages(
  queryClient: QueryClient,
  studyId: string,
): { items: DesignPointResponse[]; next_cursor?: string | null }[] | undefined {
  return queryClient.getQueryData<{
    pages: { items: DesignPointResponse[]; next_cursor?: string | null }[];
  }>(studyDesignPointsInfiniteOptions(studyId).queryKey)?.pages;
}

/**
 * Every design point in a study, with no component mounted (ENG-2397).
 *
 * The non-hook sibling of {@link useStudyDesignPoints}, and here rather than
 * at its caller for that hook's stated reason: `/studies/{id}/points` is
 * cursor-paginated and **its default page is 20**, so a caller that reads it
 * once gets a fifth of a Sobol run and no indication that it did. A cited
 * figure built that way is drawn from a subset of the runs the chart beside it
 * drew — sorted along x, which makes the loss read as a sparser sweep rather
 * than a truncated one, in a report that is filed.
 *
 * Each page is cached under its own cursor, so several figures resolved in one
 * export walk it once between them.
 */
export async function fetchAllDesignPoints(
  queryClient: QueryClient,
  studyId: string,
): Promise<DesignPointResponse[]> {
  // The open study page has already walked this endpoint into the INFINITE
  // entry, whose generated key carries `_infinite: true` and so never
  // intersects the one below. Reading it first is what keeps a citation
  // resolved on that page from re-requesting every page of a run it is
  // currently displaying.
  const pages = cachedDesignPointPages(queryClient, studyId);
  if (pages && pages.at(-1)?.next_cursor == null) {
    return pages.flatMap((page) => page.items);
  }

  // Keyed by id rather than appended blindly: a backend handing back the
  // cursor it was given serves the same page again, and a figure drawn from
  // that has each of those runs on it twice.
  const items = new Map<string, DesignPointResponse>();
  let cursor: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const response = await queryClient.fetchQuery({
      ...handleListPointsStudiesStudyIdPointsGetOptions({
        path: { study_id: studyId },
        query: { limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) },
      }),
      // A study these figures may be cited from has settled, so a page
      // already read cannot change answer.
      staleTime: Infinity,
      retry: false,
    });
    for (const item of response.items) items.set(item.id, item);
    const next: string | null = response.next_cursor ?? null;
    // Equal cursors mean the backend is not advancing; stopping beats
    // re-reading the same page until `MAX_PAGES` runs out.
    if (!next || next === cursor) break;
    cursor = next;
  }
  return [...items.values()];
}

/**
 * ONE design point, with no component mounted (ENG-2397).
 *
 * A citation that names a run carries both ids, so the whole study is the
 * wrong read: {@link fetchAllDesignPoints} walks every page of a Sobol run to
 * `.find()` one of them, which on a cold cache — an export, a fresh tab — is
 * every point of the study fetched to draw one figure of one run.
 *
 * The open study page has already walked those pages into the infinite entry,
 * so that is read FIRST and the common case costs nothing. Only a cache
 * without it reaches the endpoint, and then for one point.
 *
 * A run that is genuinely gone resolves to `null` rather than throwing: the
 * caller is a figure resolver, and every failure there degrades the citation
 * to its alt text.
 */
export async function fetchDesignPoint(
  queryClient: QueryClient,
  studyId: string,
  designPointId: string,
): Promise<DesignPointResponse | null> {
  const found = cachedDesignPointPages(queryClient, studyId)
    ?.flatMap((page) => page.items)
    .find((item) => item.id === designPointId);
  if (found) return found;

  try {
    return await queryClient.ensureQueryData({
      ...handleGetDesignPointStudiesStudyIdPointsDpIdGetOptions({
        path: { study_id: studyId, dp_id: designPointId },
      }),
      // A settled run's summary does not change under a reader, and several
      // figures citing the same run resolve onto one read.
      staleTime: Infinity,
      retry: false,
    });
  } catch {
    return null;
  }
}
